import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { Flow, SingularStationArea } from '@thive/fddm-types';

import { DEVTOOLS_OPTIONS } from '@/modules/debug/constants/zustand';

export type FlowStopType = 'flowStop' | 'area';

type StoreState = {
  currentFlowSourceId: string | null;
  currentFlowSourceFlowStopType: FlowStopType | null;
  currentFlowSourceLoadHandlingType: SingularStationArea['loadHandlingType'] | null;
  currentFlowType: Flow['type'] | null;
};

type StoreAction = {
  setCurrentFlowSourceId(id: string | null): void;
  setCurrentFlowSourceFlowStopType(type: FlowStopType): void;
  setCurrentFlowSourceLoadHandlingType(
    loadHandlingType: SingularStationArea['loadHandlingType'] | null,
  ): void;
  setCurrentFlowType(type: Flow['type'] | null): void;
  reset(): void;
};

const INITIAL_STATE: StoreState = {
  currentFlowSourceId: null,
  currentFlowSourceFlowStopType: null,
  currentFlowSourceLoadHandlingType: null,
  currentFlowType: null,
};

export const useActiveFlowStore = create<StoreState & StoreAction>()(
  devtools(
    (set) => ({
      ...INITIAL_STATE,

      setCurrentFlowSourceId(id: string) {
        set(
          {
            currentFlowSourceId: id,
          },
          undefined,
          {
            type: 'setCurrentFlowSourceId',
            ...DEVTOOLS_OPTIONS,
          },
        );
      },

      setCurrentFlowSourceFlowStopType(type: FlowStopType) {
        set(
          {
            currentFlowSourceFlowStopType: type,
          },
          undefined,
          {
            type: 'setCurrentFlowSourceFlowStopType',
            ...DEVTOOLS_OPTIONS,
          },
        );
      },

      setCurrentFlowSourceLoadHandlingType(
        loadHandlingType: SingularStationArea['loadHandlingType'],
      ) {
        set(
          {
            currentFlowSourceLoadHandlingType: loadHandlingType,
          },
          undefined,
          {
            type: 'setCurrentFlowSourceLoadHandlingType',
            ...DEVTOOLS_OPTIONS,
          },
        );
      },

      setCurrentFlowType(type: Flow['type'] | null) {
        set(
          {
            currentFlowType: type,
          },
          undefined,
          {
            type: 'setCurrentFlowType',
            ...DEVTOOLS_OPTIONS,
          },
        );
      },

      reset() {
        set(INITIAL_STATE, undefined, {
          type: 'reset',
        });
      },
    }),
    { store: 'flowStore', ...DEVTOOLS_OPTIONS },
  ),
);
